import moment from 'moment';
import { adviserFactsheetOf, qualifyingFactsheetOf } from '../../../utils/client-factsheet';
import { clientNameOf } from './clientPresentation';
import { readRecruitProfile } from './recruitProfile';
import { readClientNotes } from './clientNotesStore';
import { readRecruitStageUpdatedAt } from './recruitStage';
import { RECRUIT_ACTIVE_STAGES, RECRUIT_STALLED_DAYS } from './recruitListView';
import type { RecruitFactsMap } from './recruitListView';
import type { QueueClient } from '../../../types/client';

/**
 * 今日招募 — the recruitment half of 重點跟進.
 *
 * Same shape as the business card on purpose (TodayQueue): three things, a
 * checkbox each, a progress meter, one 「打完收工」 chip at the end. An adviser
 * should have to learn the card once.
 *
 * What is different is where the three come from. The business card reads
 * signals the SERVER already knows (a birthday, a renewal, a year of silence).
 * Recruitment has no such signals, so this reads what the adviser has recorded
 * about each candidate — the stage, the five-angle rating, the concerns they
 * voiced, whether they have taken the career quiz — and turns each into the one
 * next move it implies.
 *
 * 🔒 **The card is never empty and never short.** A recruitment list is small
 * by nature, and a workspace that greets an adviser with nothing to do on a
 * quiet week teaches them not to open it. When no candidate needs anything, the
 * last rules stop being about candidates and start being about finding some —
 * which is the honest next move at that moment anyway.
 */

/** Three, as the business card shows three. */
export const RECRUIT_SUGGESTION_SIZE = 3;

/**
 * Below this, 「go and find more」 outranks most of what is on file. Five is
 * what the user set: enough that a pipeline exists, few enough to be reachable.
 */
export const RECRUIT_TARGET_CANDIDATES = 5;

/** 暫緩 this long has stopped being a pause and become a decision not taken. */
export const RECRUIT_PAUSED_STALE_DAYS = 60;

/**
 * Where a suggestion's CTA goes. Either an action the dispatcher already
 * handles, or one of the drawer's recruitment surfaces, which are readouts
 * mounted while the drawer is open rather than destinations it can dispatch to
 * (see clientEntitlements' note on `recruit-idea`).
 */
export type RecruitSuggestionTarget =
  | { kind: 'action'; action: 'meeting-note' | 'contact' }
  | { kind: 'surface'; surface: 'idea' | 'score' | 'quiz' | 'drawer' }
  /** Book-level: nothing to open, the page itself answers. */
  | { kind: 'browse' };

export type RecruitSuggestionKind =
  | 'quiz'
  | 'stalled'
  | 'priorityNotStarted'
  | 'concern'
  | 'unrated'
  | 'pausedLong'
  | 'fillIn'
  | 'sourcing'
  | 'referral'
  | 'fallback';

export type RecruitSuggestion = {
  /** Stable across renders so the checkbox state survives a re-sort. */
  id: string;
  kind: RecruitSuggestionKind;
  /** The person it is about; null for the two book-level ones. */
  client: QueueClient | null;
  messageId: string;
  values: Record<string, string | number>;
  ctaMessageId: string;
  target: RecruitSuggestionTarget;
};

export type RecruitSuggestionsInput = {
  candidates: QueueClient[];
  facts: RecruitFactsMap;
  now?: moment.MomentInput;
  adviserEmail?: string;
};

const CTA = {
  quiz: 'clientv2-recruit-cta-quiz',
  sendQuiz: 'clientv2-recruit-cta-send-quiz',
  meeting: 'clientv2-recruit-cta-meeting',
  contact: 'clientv2-recruit-cta-contact',
  idea: 'clientv2-recruit-cta-idea',
  score: 'clientv2-recruit-cta-score',
  stage: 'clientv2-recruit-cta-stage',
  browse: 'clientv2-recruit-cta-browse'
};

const daysBetween = (from: moment.MomentInput, to: moment.Moment): number | null => {
  const start = moment(from);
  if (!start.isValid()) return null;
  return to.clone().startOf('day').diff(start.clone().startOf('day'), 'days');
};

/**
 * 家人反對 leads its group — it is the one concern the candidate cannot settle
 * alone (design doc §6.2), so it needs a second person in the room and is worth
 * raising before the ones they can think their way out of. The rest keep the
 * table's own order.
 */
const CONCERN_ORDER = [
  'family',
  'unstable',
  'noClients',
  'rejection',
  'stableJob',
  'seniority',
  'image',
  'notSales',
  'exam',
  'attrition'
];

const firstConcernOf = (clientId: string): string | null => {
  const concerns = readRecruitProfile(clientId).concerns;
  if (!concerns.length) return null;
  const ranked = CONCERN_ORDER.filter(key => concerns.includes(key));
  return ranked[0] || concerns[0];
};

/**
 * Built in priority order, one candidate at most once, then cut to three.
 *
 * The order is the argument of the card: a candidate who has just acted
 * (finished the quiz) outranks one the adviser has let go cold, which outranks
 * one who is ready but untouched, which outranks the paperwork.
 */
export const buildRecruitSuggestions = (input: RecruitSuggestionsInput): RecruitSuggestion[] => {
  const { candidates, facts, adviserEmail } = input;
  const now = moment(input.now);
  const suggestions: RecruitSuggestion[] = [];
  const used = new Set<string>();

  const push = (
    client: QueueClient | null,
    kind: RecruitSuggestionKind,
    messageId: string,
    values: Record<string, string | number>,
    ctaMessageId: string,
    target: RecruitSuggestionTarget
  ) => {
    // One row per person. Mary needing four things is still one line on a card
    // of three — the other two lines are worth more spent on other people.
    if (client && used.has(client._id)) return;
    if (client) used.add(client._id);
    suggestions.push({ id: `${kind}-${client?._id ?? 'book'}`, kind, client, messageId, values, ctaMessageId, target });
  };

  const stageOf = (client: QueueClient) => facts.get(client._id)?.stage ?? null;
  const summaryOf = (client: QueueClient) => facts.get(client._id)?.summary;
  const lastMeetingOf = (client: QueueClient) => qualifyingFactsheetOf(client, adviserEmail)?.latestMeetingDate || null;

  // 1 — the candidate acted. A finished quiz is the only signal on this card
  // that came from THEM, which is why it outranks everything the adviser has
  // or has not done.
  candidates.forEach(client => {
    const completedAt = readClientNotes(client._id)?.quizCompletedAt.career;
    if (!completedAt) return;
    const days = daysBetween(completedAt, now);
    if (days === null || days < 0) return;
    const meeting = lastMeetingOf(client);
    // Discussed already if a meeting was recorded after it was taken.
    if (meeting && moment(meeting).isSameOrAfter(moment(completedAt), 'day')) return;
    push(client, 'quiz', 'clientv2-recruit-suggest-quiz', { name: clientNameOf(client), days }, CTA.quiz, {
      // 問卷對話框由抽屜掛著,不是派發得出去的目的地(和招募 IDEA 同一種東西)。
      // 之前寫成 action 的結果是:按下去,什麼都沒有發生。
      kind: 'surface',
      surface: 'quiz'
    });
  });

  // 2 — open conversations going cold. The same definition the 停滯 tile counts,
  // so the card and the tile can never disagree about who is stalled.
  candidates.forEach(client => {
    const stage = stageOf(client);
    if (stage !== 'contact' && stage !== 'talking') return;
    const meeting = lastMeetingOf(client);
    const days = meeting ? daysBetween(meeting, now) : null;
    if (days !== null && days < RECRUIT_STALLED_DAYS) return;
    push(
      client,
      'stalled',
      meeting ? 'clientv2-recruit-suggest-stalled' : 'clientv2-recruit-suggest-stalled-never',
      { name: clientNameOf(client), days: days ?? 0 },
      CTA.meeting,
      { kind: 'action', action: 'meeting-note' }
    );
  });

  // 3 — rated well, and nobody has begun. The most expensive miss on the page.
  candidates.forEach(client => {
    const summary = summaryOf(client);
    const stage = stageOf(client);
    if (summary?.tier !== 'priority') return;
    if (stage !== null && stage !== 'notStarted') return;
    push(
      client,
      'priorityNotStarted',
      'clientv2-recruit-suggest-priority',
      { name: clientNameOf(client), total: summary.total ?? 0 },
      CTA.contact,
      { kind: 'action', action: 'contact' }
    );
  });

  // 4 — a concern on file that nothing has been done about. The answer is in
  // the app already (design doc §6.2), so the card names it rather than leaving
  // the adviser to remember the table.
  candidates.forEach(client => {
    const concern = firstConcernOf(client._id);
    if (!concern) return;
    push(
      client,
      'concern',
      'clientv2-recruit-suggest-concern',
      {
        name: clientNameOf(client),
        concernId: `clientv2-recruit-concern-${concern}`,
        answerId: `clientv2-recruit-concern-${concern}-answer`
      },
      CTA.idea,
      { kind: 'surface', surface: 'idea' }
    );
  });

  // 5 — being worked, still unrated. Paperwork, and it ranks like paperwork.
  // Scoped to the active stages, the same set the 待評估 tile counts: rating
  // someone who has already joined, or who is parked, is not a next move.
  candidates.forEach(client => {
    const summary = summaryOf(client);
    const stage = stageOf(client);
    if (!summary || summary.total !== null) return;
    if (stage === null || !(RECRUIT_ACTIVE_STAGES as readonly string[]).includes(stage)) return;
    push(
      client,
      'unrated',
      'clientv2-recruit-suggest-unrated',
      { name: clientNameOf(client), rated: summary.rated },
      CTA.score,
      { kind: 'surface', surface: 'score' }
    );
  });

  // 6 — parked long enough that the pause has become a decision nobody took.
  // Counted from the day 暫緩 was SET (stageUpdatedAt, §7) — counting from the
  // last meeting read 「暫緩了 150 天」 the day the adviser chose it.
  candidates.forEach(client => {
    if (stageOf(client) !== 'paused') return;
    const since = readRecruitStageUpdatedAt(client._id);
    const days = since ? daysBetween(since, now) : null;
    if (days === null || days < RECRUIT_PAUSED_STALE_DAYS) return;
    push(client, 'pausedLong', 'clientv2-recruit-suggest-paused', { name: clientNameOf(client), days }, CTA.stage, {
      kind: 'surface',
      surface: 'drawer'
    });
  });

  // 6½ — 補資料: a candidate the adviser knows almost nothing about — no DISC,
  // nothing on the profile, no quiz. The cheapest way to fill that in is not
  // the adviser typing: one career quiz brings the DISC, the concerns and the
  // strengths in by itself (applyQuizToRecruitProfile). Joined and paused are
  // out, as they are for every other chase on this card. So is a candidate
  // whose quiz link is already out and still live: asking again would only
  // retire the link they have.
  candidates.forEach(client => {
    const stage = stageOf(client);
    if (stage === 'joined' || stage === 'paused') return;
    if (adviserFactsheetOf(client, adviserEmail)?.personality) return;
    const notes = readClientNotes(client._id);
    if (notes?.quizCompletedAt.career) return;
    const pendingUntil = notes?.quizPendingUntil?.career;
    if (pendingUntil && moment(pendingUntil).isAfter(now)) return;
    const profile = readRecruitProfile(client._id);
    if (profile.concerns.length || profile.painPoints.length || profile.strengths.length) return;
    push(client, 'fillIn', 'clientv2-recruit-suggest-fill-in', { name: clientNameOf(client) }, CTA.sendQuiz, {
      kind: 'surface',
      surface: 'quiz'
    });
  });

  // 7 — not enough people in the pipeline. Deliberately ABOVE the last two:
  // once the book is this thin, finding one more candidate is worth more than
  // any single tidy-up on the few already there.
  if (candidates.length < RECRUIT_TARGET_CANDIDATES) {
    push(
      null,
      'sourcing',
      'clientv2-recruit-suggest-sourcing',
      { count: candidates.length, target: RECRUIT_TARGET_CANDIDATES },
      CTA.browse,
      { kind: 'browse' }
    );
  }

  // 8 — the warmest source of the next candidate is the last one who joined.
  candidates.forEach(client => {
    if (stageOf(client) !== 'joined') return;
    push(client, 'referral', 'clientv2-recruit-suggest-referral', { name: clientNameOf(client) }, CTA.contact, {
      kind: 'action',
      action: 'contact'
    });
  });

  // 9 — nothing on file needs anything. Rather than an empty card, the honest
  // next move: go and look at the book for someone who might fit.
  push(null, 'fallback', 'clientv2-recruit-suggest-fallback', {}, CTA.browse, { kind: 'browse' });

  return suggestions.slice(0, RECRUIT_SUGGESTION_SIZE);
};
